import React, {
  useState,
  useEffect,
  useContext,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import Select from "react-select";
import Table from "../../../ui/Table";
import Modal from "../../../ui/Modal";
import Loader from "../../../ui/Loader";
import COPOMapping from "../../../ui/COPOMapping";
import { AuthContext } from "../../../../context/AuthContext";
import {
  swalConfirm,
  swalError,
  swalSuccess,
  swalWarning,
} from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";


const ManageCourses = forwardRef((props, ref) => {
  const { user } = useContext(AuthContext);
  const [courses, setCourses] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [hasLevels, setHasLevels] = useState(false);

  const [newCourse, setNewCourse] = useState({
    name: "",
    codeNumber: "",
    level: "",
    departmentId: "",
    programId: "",
    outcomes: [],
    COPOMapping: [],
  });

  const [editCourse, setEditCourse] = useState({
    id: "",
    name: "",
    codeNumber: "",
    level: "",
    departmentId: "",
    programId: "",
    outcomes: [],
    COPOMapping: [],
  });

  const [initialEditCourse, setInitialEditCourse] = useState(null);
  const [isEditChanged, setIsEditChanged] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const [departmentsMap, setDepartmentsMap] = useState({});
  const [allDepartments, setAllDepartments] = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [selectedProgramData, setSelectedProgramData] = useState(null);
  const [selectedEditProgramData, setSelectedEditProgramData] = useState(null);
  const [levels, setLevels] = useState([]);
  const [levelsFetched, setLevelsFetched] = useState(false);

  const [computedCourseCode, setComputedCourseCode] = useState("");
  const [computedEditCourseCode, setComputedEditCourseCode] = useState("");

  const enhancedCourses = courses.map((course) => {
    const dept = departmentsMap[course.departmentId];
    const departmentPrefix = dept ? dept.prefix : "";
    const program = allPrograms.find((p) => p._id === course.programId);
    const programName = program ? program.name : "";

    return {
      ...course,
      level: course.level,
      departmentPrefix: departmentPrefix,
      programName: programName,
      edit: (
        <button
          className="edit-btn"
          onClick={async () => {
            setEditingId(course._id);
            const dept = departmentsMap[course.departmentId];
            if (course.departmentId) {
              await fetchAndSetDepartment(course.departmentId);
            }
            if (course.programId) {
              await fetchProgramDetails(course.programId, true);
            }

            const codeNumber = dept
              ? course.code.replace(dept.prefix, "")
              : course.code.replace(/^[A-Z]+/, "");

            const courseData = {
              id: course._id,
              name: course.name,
              codeNumber: codeNumber,
              level: course.level,
              departmentId: course.departmentId || "",
              programId: course.programId || "",
              outcomes: course.outcomes || [{ code: "", description: "" }],
              COPOMapping: course.COPOMapping || [],
            };

            setEditCourse(courseData);
            setInitialEditCourse(courseData);
            setComputedEditCourseCode(course.code);
            setIsEditModalOpen(true);
            setIsEditChanged(false);
            setEditingId(null);
          }}
          disabled={deletingId !== null || editingId !== null}
        >
          {editingId === course._id ? (
            <i className="fa-solid fa-spinner fa-spin"></i>
          ) : (
            <i className="fa-solid fa-pen-to-square"></i>
          )}
        </button>
      ),
      delete: (
        <button
          className="delete-btn"
          onClick={() => handleDelete(course._id)}
          disabled={deletingId !== null || editingId !== null}
        >
          {deletingId === course._id ? (
            <i className="fa-solid fa-spinner fa-spin"></i>
          ) : (
            <i className="fa-solid fa-trash-can"></i>
          )}
        </button>
      ),
    };
  });

  const columns = [
    {
      key: "name",
      label: "Course Name",
      sortable: true,
    },
    {
      key: "code",
      label: "Course Code",
      sortable: true,
    },
    {
      key: "level",
      label: "Level",
      sortable: true,
    },
    {
      key: "departmentPrefix",
      label: "Department",
      sortable: true,
    },
    {
      key: "programName",
      label: "Program",
      sortable: true,
    },
    {
      key: "edit",
      label: "",
      sortable: false,
    },
    {
      key: "delete",
      label: "",
      sortable: false,
    },
  ];

  useImperativeHandle(ref, () => ({
    openModal: () => setIsModalOpen(true),
  }));

  const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

  // Fetch institute data
  useEffect(() => {
    
    const fetchInstituteData = async () => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${user.instituteId}`
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to fetch institute data");
        const data = await response.json();
        
        if (data.levels && data.levels.length > 0) {
          setHasLevels(true);
          setLevels(
            data.levels.map((level) => ({
              value: level,
              label: level,
            }))
          );
        } else {
          setHasLevels(false);
        }
      } catch (err) {
        swalError(err.message);
      }
    };
    fetchInstituteData();
  }, [user.instituteId, serverUrl]);
  // Fetch all programs
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/program/get-all/${user.instituteId}`
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to fetch programs");
        const data = await response.json();
        setAllPrograms(data);
      } catch (err) {
        swalError(err.message);
      }
    };

    fetchPrograms();
  }, [user.instituteId, serverUrl]);

  // Fetch program details with PO template
  const fetchProgramDetails = useCallback(
    async (programId, isEditMode = false) => {
      if (!programId) {
        if (isEditMode) {
          setSelectedEditProgramData(null);
        } else {
          setSelectedProgramData(null);
        }
        return;
      }

      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/program/get/${programId}`
        );
        if (!response || !response.ok) {
          throw new Error("Failed to fetch program details");
        }
        const programData = await response.json();

        // Combine PO and PSO outcomes directly from programData
        const allOutcomes = [
          ...(programData.programOutcomes || []),
          ...(programData.PSOutcomes || []),
        ];

        const programWithOutcomes = {
          ...programData,
          programOutcomes: allOutcomes,
        };

        if (isEditMode) {
          setSelectedEditProgramData(programWithOutcomes);
        } else {
          setSelectedProgramData(programWithOutcomes);
        }
      } catch (err) {
        swalError(err.message);
        if (isEditMode) {
          setSelectedEditProgramData(null);
        } else {
          setSelectedProgramData(null);
        }
      }
    },
    [serverUrl]
  );

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/course/get-all/${user.instituteId}`
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to fetch courses");
        const data = await response.json();
        setCourses(data);
        setError(null);
      } catch (err) {
        setError(err.message);
        swalError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [user.instituteId, serverUrl]);

  const fetchLevels = useCallback(async () => {
    if (levelsFetched) return;
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/lov/get-all-by-type/level`
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch levels");
      const data = await response.json();
      if (!hasLevels) {
        setLevels(
          data
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((level) => ({
              value: level.value,
              label: level.value,
            }))
        );
      }
      setLevelsFetched(true);
    } catch (err) {
      swalError(err.message);
    }
  }, [levelsFetched, serverUrl, hasLevels]);

  useEffect(() => {
    if (isModalOpen || isEditModalOpen) {
      fetchLevels();
    }
  }, [isModalOpen, isEditModalOpen, fetchLevels]);

  useEffect(() => {
    const fetchDepartmentDetails = async () => {
      const departmentIds = courses
        .filter(
          (course) =>
            course.departmentId && !departmentsMap[course.departmentId]
        )
        .map((course) => course.departmentId);

      const uniqueDepartmentIds = [...new Set(departmentIds)];

      await Promise.all(
        uniqueDepartmentIds.map(async (deptId) => {
          try {
            const response = await fetchWithAuth(
              `${serverUrl}/api/department/get/${deptId}`
            );
            if (!response) return;
            if (!response.ok)
              throw new Error("Failed to fetch department details");
            const data = await response.json();
            setDepartmentsMap((prev) => ({ ...prev, [deptId]: data }));
          } catch (err) {
            swalError(err.message);
          }
        })
      );
    };

    if (courses.length > 0) {
      fetchDepartmentDetails();
    }
  }, [courses, departmentsMap, serverUrl]);

  useEffect(() => {
    const fetchAllDepartments = async () => {
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/department/get-all/${user.instituteId}`
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to fetch all departments");
        const data = await response.json();
        setAllDepartments(data);
      } catch (err) {
        swalError(err.message);
      }
    };

    fetchAllDepartments();
  }, [user.instituteId, serverUrl]);

  useEffect(() => {
    if (newCourse.departmentId && departmentsMap[newCourse.departmentId]) {
      const selectedDept = departmentsMap[newCourse.departmentId];
      if (selectedDept && newCourse.codeNumber) {
        setComputedCourseCode(`${selectedDept.prefix}${newCourse.codeNumber}`);
      } else {
        setComputedCourseCode("");
      }
    } else if (newCourse.name && newCourse.codeNumber) {
      const courseNamePart = newCourse.name
        .trim()
        .substring(0, 3)
        .toUpperCase();
      setComputedCourseCode(`${courseNamePart}${newCourse.codeNumber}`);
    } else {
      setComputedCourseCode("");
    }
  }, [
    departmentsMap,
    newCourse.departmentId,
    newCourse.codeNumber,
    newCourse.name,
  ]);

  useEffect(() => {
    if (editCourse.departmentId && departmentsMap[editCourse.departmentId]) {
      const selectedDept = departmentsMap[editCourse.departmentId];
      if (selectedDept && editCourse.codeNumber) {
        setComputedEditCourseCode(
          `${selectedDept.prefix}${editCourse.codeNumber}`
        );
      } else {
        setComputedEditCourseCode("");
      }
    } else if (editCourse.name && editCourse.codeNumber) {
      const courseNamePart = editCourse.name
        .trim()
        .substring(0, 3)
        .toUpperCase();
      setComputedEditCourseCode(`${courseNamePart}${editCourse.codeNumber}`);
    } else {
      setComputedEditCourseCode("");
    }
  }, [
    departmentsMap,
    editCourse.departmentId,
    editCourse.codeNumber,
    editCourse.name,
  ]);

  useEffect(() => {
    if (editCourse.departmentId && departmentsMap[editCourse.departmentId]) {
      const selectedDept = departmentsMap[editCourse.departmentId];
      if (selectedDept && editCourse.codeNumber) {
        setComputedEditCourseCode(
          `${selectedDept.prefix}${editCourse.codeNumber}`
        );
      } else {
        setComputedEditCourseCode("");
      }
    } else if (editCourse.name && editCourse.codeNumber) {
      const courseNamePart = editCourse.name
        .trim()
        .substring(0, 3)
        .toUpperCase();
      setComputedEditCourseCode(`${courseNamePart}${editCourse.codeNumber}`);
    } else {
      setComputedEditCourseCode("");
    }
  }, [
    departmentsMap,
    editCourse.departmentId,
    editCourse.codeNumber,
    editCourse.name,
  ]);

  // COPO Mapping handlers for create form
  const handleCOPOMappingChange = (coCode, poCode, weight) => {
    setNewCourse((prev) => {
      const existingMapping = prev.COPOMapping.find(
        (m) => m.COCode === coCode && m.POCode === poCode
      );

      let updatedMapping;
      if (weight === 0) {
        updatedMapping = prev.COPOMapping.filter(
          (m) => !(m.COCode === coCode && m.POCode === poCode)
        );
      } else if (existingMapping) {
        updatedMapping = prev.COPOMapping.map((m) =>
          m.COCode === coCode && m.POCode === poCode ? { ...m, weight } : m
        );
      } else {
        updatedMapping = [
          ...prev.COPOMapping,
          { COCode: coCode, POCode: poCode, weight },
        ];
      }

      return { ...prev, COPOMapping: updatedMapping };
    });
  };

  const handleCOChange = (index, field, value) => {
    setNewCourse((prev) => {
      const updatedOutcomes = [...prev.outcomes];
      updatedOutcomes[index] = {
        ...updatedOutcomes[index],
        [field]: value,
      };
      return { ...prev, outcomes: updatedOutcomes };
    });
  };

  const handleCOAdd = () => {
    setNewCourse((prev) => ({
      ...prev,
      outcomes: [...prev.outcomes, { code: "", description: "" }],
    }));
  };

  const handleCORemove = (index) => {
    if (newCourse.programId && newCourse.outcomes.length <= 1) {
      swalWarning("At least one course outcome is required.");
      return;
    }

    const removedCO = newCourse.outcomes[index];
    setNewCourse((prev) => ({
      ...prev,
      outcomes: prev.outcomes.filter((_, i) => i !== index),
      COPOMapping: prev.COPOMapping.filter((m) => m.COCode !== removedCO.code),
    }));
  };

  // COPO Mapping handlers for edit form
  const handleEditCOPOMappingChange = (coCode, poCode, weight) => {
    setEditCourse((prev) => {
      const existingMapping = prev.COPOMapping.find(
        (m) => m.COCode === coCode && m.POCode === poCode
      );

      let updatedMapping;
      if (weight === 0) {
        updatedMapping = prev.COPOMapping.filter(
          (m) => !(m.COCode === coCode && m.POCode === poCode)
        );
      } else if (existingMapping) {
        updatedMapping = prev.COPOMapping.map((m) =>
          m.COCode === coCode && m.POCode === poCode ? { ...m, weight } : m
        );
      } else {
        updatedMapping = [
          ...prev.COPOMapping,
          { COCode: coCode, POCode: poCode, weight },
        ];
      }

      return { ...prev, COPOMapping: updatedMapping };
    });
  };

  const handleEditCOChange = (index, field, value) => {
    setEditCourse((prev) => {
      const updatedOutcomes = [...prev.outcomes];
      updatedOutcomes[index] = {
        ...updatedOutcomes[index],
        [field]: value,
      };
      return { ...prev, outcomes: updatedOutcomes };
    });
  };

  const handleEditCOAdd = () => {
    setEditCourse((prev) => ({
      ...prev,
      outcomes: [...prev.outcomes, { code: "", description: "" }],
    }));
  };

  const handleEditCORemove = (index) => {
    if (editCourse.programId && editCourse.outcomes.length <= 1) {
      swalWarning("At least one course outcome is required.");
      return;
    }

    const removedCO = editCourse.outcomes[index];
    setEditCourse((prev) => ({
      ...prev,
      outcomes: prev.outcomes.filter((_, i) => i !== index),
      COPOMapping: prev.COPOMapping.filter((m) => m.COCode !== removedCO.code),
    }));
  };

  const validateOutcomes = (outcomes) => {
    if (!outcomes || outcomes.length === 0) {
      swalError("At least one course outcome is required.");
      return false;
    }

    for (let i = 0; i < outcomes.length; i++) {
      if (!outcomes[i].code.trim() || !outcomes[i].description.trim()) {
        swalError(`Outcome ${i + 1}: Both code and description are required.`);
        return false;
      }
    }

    const codes = outcomes.map((outcome) => outcome.code.trim());
    const uniqueCodes = new Set(codes);
    if (codes.length !== uniqueCodes.size) {
      swalError("Each outcome must have a unique code.");
      return false;
    }

    return true;
  };

  const handleDelete = async (courseId) => {
    const result = await swalConfirm(
      "Are you sure you want to delete this course?",
      "You won't be able to revert this!",
      "Yes, delete it!",
      "Cancel"
    );

    if (result.isConfirmed) {
      setDeletingId(courseId);
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/course/delete/${courseId}`,
          {
            method: "DELETE",
          }
        );
        if (!response) return;
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to delete course");
        }
        setCourses(courses.filter((course) => course._id !== courseId));
        swalSuccess("Course deleted successfully.");
      } catch (err) {
        swalError(err.message || "Failed to delete course");
      } finally {
        setDeletingId(null);
      }
    }
  };

  const sortCOPOMappings = (mappings) => {
    return mappings
      .filter((m) => m.weight > 0)
      .filter((m) => !m.COCode.startsWith("_temp_"))
      .sort((a, b) => {
        const coCodeA = a.COCode.toLowerCase();
        const coCodeB = b.COCode.toLowerCase();
        if (coCodeA < coCodeB) return -1;
        if (coCodeA > coCodeB) return 1;

        const poCodeA = a.POCode.toLowerCase();
        const poCodeB = b.POCode.toLowerCase();
        if (poCodeA < poCodeB) return -1;
        if (poCodeA > poCodeB) return 1;

        return 0;
      });
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();

    if (
      !newCourse.name.trim() ||
      !newCourse.codeNumber.toString().trim() ||
      !newCourse.level.trim()
    ) {
      swalError("Please provide course name, code number, and level.");
      return;
    }

    // Only validate outcomes if a program is selected
    if (newCourse.programId && !validateOutcomes(newCourse.outcomes)) {
      return;
    }

    setIsCreating(true);
    try {
      const sortedCOPOMapping = sortCOPOMappings(newCourse.COPOMapping);

      const response = await fetchWithAuth(`${serverUrl}/api/course/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: newCourse.name,
          code: computedCourseCode,
          level: newCourse.level,
          departmentId: newCourse.departmentId || undefined,
          programId: newCourse.programId || undefined,
          outcomes: newCourse.outcomes,
          COPOMapping: sortedCOPOMapping,
          instituteId: user.instituteId,
        }),
      });
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create course");
      }
      const createdCourse = await response.json();
      setCourses([...courses, createdCourse]);
      setIsModalOpen(false);
      setNewCourse({
        name: "",
        codeNumber: "",
        level: "",
        departmentId: "",
        programId: "",
        outcomes: [],
        COPOMapping: [],
      });
      setComputedCourseCode("");
      setSelectedProgramData(null);
      swalSuccess("Course created successfully");
    } catch (err) {
      swalError(err.message || "Failed to create course");
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditCourse = async (e) => {
    e.preventDefault();

    if (
      !editCourse.name.trim() ||
      !editCourse.codeNumber.toString().trim() ||
      !editCourse.level.trim()
    ) {
      swalError("Please provide course name, code number, and level.");
      return;
    }

    // Only validate outcomes if a program is selected
    if (editCourse.programId && !validateOutcomes(editCourse.outcomes)) {
      return;
    }

    setIsUpdating(true);
    try {
      const sortedCOPOMapping = sortCOPOMappings(editCourse.COPOMapping);

      const response = await fetchWithAuth(
        `${serverUrl}/api/course/update/${editCourse.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: editCourse.name,
            code: computedEditCourseCode,
            level: editCourse.level,
            departmentId: editCourse.departmentId || undefined,
            programId: editCourse.programId || undefined,
            outcomes: editCourse.outcomes,
            COPOMapping: sortedCOPOMapping,
            instituteId: user.instituteId,
          }),
        }
      );
      if (!response) return;
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update course");
      }
      const updatedCourse = await response.json();
      setCourses(
        courses.map((course) =>
          course._id === updatedCourse._id ? updatedCourse : course
        )
      );
      setIsEditModalOpen(false);
      setEditCourse({
        id: "",
        name: "",
        codeNumber: "",
        level: "",
        departmentId: "",
        programId: "",
        outcomes: [],
        COPOMapping: [],
      });
      setInitialEditCourse(null);
      setComputedEditCourseCode("");
      setIsEditChanged(false);
      setSelectedEditProgramData(null);
      swalSuccess("Course updated successfully");
    } catch (err) {
      swalError("Failed to update course");
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    if (editCourse && initialEditCourse) {
      const isBasicChanged =
        editCourse.name !== initialEditCourse.name ||
        editCourse.codeNumber.toString() !==
        initialEditCourse.codeNumber.toString() ||
        editCourse.level !== initialEditCourse.level ||
        editCourse.departmentId !== initialEditCourse.departmentId ||
        editCourse.programId !== initialEditCourse.programId;

      const isOutcomesChanged =
        JSON.stringify(editCourse.outcomes) !==
        JSON.stringify(initialEditCourse.outcomes);

      const isMappingChanged =
        JSON.stringify(editCourse.COPOMapping) !==
        JSON.stringify(initialEditCourse.COPOMapping);

      setIsEditChanged(isBasicChanged || isOutcomesChanged || isMappingChanged);
    }
  }, [editCourse, initialEditCourse]);

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditCourse((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateInputChange = (e) => {
    const { name, value } = e.target;
    setNewCourse({ ...newCourse, [name]: value });
  };

  const handleComputedCourseCodeChange = (e) => {
    setComputedCourseCode(e.target.value);
  };

  const handleComputedEditCourseCodeChange = (e) => {
    setComputedEditCourseCode(e.target.value);
  };

  const departmentOptions = allDepartments.map((dept) => ({
    value: dept._id,
    label: `${dept.prefix} - ${dept.name}`,
  }));

  const programOptions = allPrograms.map((program) => ({
    value: program._id,
    label: program.name,
  }));

  const fetchAndSetDepartment = useCallback(
    async (deptId) => {
      if (departmentsMap[deptId]) return;
      try {
        const response = await fetchWithAuth(
          `${serverUrl}/api/department/get/${deptId}`
        );
        if (!response) return;
        if (!response.ok) throw new Error("Failed to fetch department details");
        const data = await response.json();
        setDepartmentsMap((prev) => ({ ...prev, [deptId]: data }));
      } catch (err) {
        swalError(err.message);
      }
    },
    [departmentsMap, serverUrl]
  );

  return (
    <>
      <div className="header">
        <button className="create-btn" onClick={() => setIsModalOpen(true)}>
          Add Course +
        </button>
      </div>
      <div className="manage-container">
        {loading && <Loader />}
        {error && <div className="error-message">{error}</div>}
        {!loading && !error && (
          <Table data={enhancedCourses} columns={columns} caption="Courses" />
        )}
      </div>

      {/* Add Course Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => !isCreating && setIsModalOpen(false)}
        maxWidth={newCourse.programId ? "100%" : "default"}
      >
        <h2>Add New Course</h2>
        <p className="modal-description" style={{ marginBottom: "1rem" }}>
          Fill in the details below to add a new course to your institute.
        </p>
        <form onSubmit={handleCreateCourse} className="course-form">
          {allPrograms.length > 0 && (
            <div className="form-group">
              <label htmlFor="program">
                Program <span className="optional-text">(Optional)</span>
              </label>
              <Select
                id="program"
                name="programId"
                options={programOptions}
                value={programOptions.find(
                  (option) => option.value === newCourse.programId
                )}
                onChange={async (selectedOption) => {
                  const programId = selectedOption ? selectedOption.value : "";
                  setNewCourse({
                    ...newCourse,
                    programId: programId,
                    // Clear CO-PO mapping when program is cleared
                    COPOMapping: programId ? newCourse.COPOMapping : [],
                  });
                  await fetchProgramDetails(programId, false);
                }}
                isClearable
                placeholder="Select program for CO-PO mapping..."
                isDisabled={isCreating}
              />
              <small className="helper-text">
                Select a program to enable CO-PO mapping.
              </small>
            </div>
          )}
          {allDepartments.length > 0 && (
            <div className="form-group">
              <label htmlFor="department">
                Department <span className="optional-text">(Optional)</span>
              </label>
              <Select
                id="department"
                name="departmentId"
                options={departmentOptions}
                value={departmentOptions.find(
                  (option) => option.value === newCourse.departmentId
                )}
                onChange={async (selectedOption) => {
                  const deptId = selectedOption ? selectedOption.value : "";
                  setNewCourse({
                    ...newCourse,
                    departmentId: deptId,
                  });
                  if (deptId) {
                    await fetchAndSetDepartment(deptId);
                  }
                }}
                isClearable
                placeholder="Select or search department..."
                isDisabled={isCreating}
              />
              <small className="helper-text">
                You can search for a department by name or code.
              </small>
            </div>
          )}
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Course Name
            </label>
            <input
              type="text"
              name="name"
              value={newCourse.name}
              placeholder="Enter the course name"
              onChange={handleCreateInputChange}
              required
              disabled={isCreating}
            />
          </div>

          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Course
              Suffix
            </label>
            <input
              type="number"
              name="codeNumber"
              value={newCourse.codeNumber}
              placeholder="e.g. 101, 201"
              onChange={handleCreateInputChange}
              required
              disabled={isCreating}
            />
            <small className="helper-text">
              Enter the numeric part of the course code (e.g. 101, 201)
            </small>
          </div>

          {computedCourseCode && (
            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> Course
                Code
              </label>
              <input
                type="text"
                value={computedCourseCode.toUpperCase()}
                readOnly={!!newCourse.departmentId || isCreating}
                onChange={
                  !newCourse.departmentId
                    ? handleComputedCourseCodeChange
                    : undefined
                }
                disabled={isCreating}
              />
              <small className="helper-text">
                This code is auto-generated based on department and suffix.
              </small>
            </div>
          )}

          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Level
            </label>
            <Select
              id="level"
              name="level"
              options={levels}
              value={levels.find((option) => option.value === newCourse.level)}
              onChange={(selectedOption) =>
                setNewCourse({
                  ...newCourse,
                  level: selectedOption ? selectedOption.value : "",
                })
              }
              isClearable
              placeholder="Select level..."
              required
              isDisabled={isCreating}
            />
          </div>

          {/* CO-PO Mapping Section - Only show when program is selected */}
          {newCourse.programId && selectedProgramData && (
            <COPOMapping
              courseOutcomes={newCourse.outcomes}
              programOutcomes={selectedProgramData.programOutcomes || []}
              copoMapping={newCourse.COPOMapping}
              onCOPOMappingChange={handleCOPOMappingChange}
              onCOChange={handleCOChange}
              onCOAdd={handleCOAdd}
              onCORemove={handleCORemove}
              isDisabled={isCreating}
            />
          )}

          {isCreating ? (
            <Loader />
          ) : (
            <button type="submit" className="btn-submit">
              Create Course
            </button>
          )}
        </form>
      </Modal>

      {/* Edit Course Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => !isUpdating && setIsEditModalOpen(false)}
        maxWidth={editCourse.programId ? "100%" : "default"}
      >
        <h2>Edit Course</h2>
        <p className="modal-description" style={{ marginBottom: "1rem" }}>
          Update the details below to edit the course information.
        </p>
        <form onSubmit={handleEditCourse} className="course-form">
          {allPrograms.length > 0 && (
            <div className="form-group">
              <label htmlFor="program">
                Program <span className="optional-text">(Optional)</span>
              </label>
              <Select
                id="program"
                name="programId"
                options={programOptions}
                value={
                  programOptions.find(
                    (option) => option.value === editCourse.programId
                  ) || null
                }
                onChange={async (selectedOption) => {
                  const programId = selectedOption ? selectedOption.value : "";
                  setEditCourse((prev) => ({
                    ...prev,
                    programId: programId,
                    // Clear CO-PO mapping when program is cleared
                    COPOMapping: programId ? prev.COPOMapping : [],
                  }));
                  await fetchProgramDetails(programId, true);
                }}
                placeholder="Select program for CO-PO mapping..."
                isDisabled={isUpdating}
              />
              <small className="helper-text">
                Select a program to enable CO-PO mapping.
              </small>
            </div>
          )}

          {allDepartments.length > 0 && (
            <div className="form-group">
              <label htmlFor="department">
                Department <span className="optional-text">(Optional)</span>
              </label>
              <Select
                id="department"
                name="departmentId"
                options={departmentOptions}
                value={
                  departmentOptions.find(
                    (option) => option.value === editCourse.departmentId
                  ) || null
                }
                onChange={async (selectedOption) => {
                  const deptId = selectedOption ? selectedOption.value : "";
                  setEditCourse((prev) => ({
                    ...prev,
                    departmentId: deptId,
                  }));
                  if (deptId) {
                    await fetchAndSetDepartment(deptId);
                  }
                }}
                placeholder="Select or search department..."
                isDisabled={isUpdating}
              />
              <small className="helper-text">
                You can search for a department by name or code.
              </small>
            </div>
          )}

          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Course Name
            </label>
            <input
              type="text"
              name="name"
              value={editCourse.name}
              placeholder="Enter the course name"
              onChange={handleEditInputChange}
              required
              disabled={isUpdating}
            />
          </div>

          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Course
              Suffix
            </label>
            <input
              type="number"
              name="codeNumber"
              value={editCourse.codeNumber}
              placeholder="e.g. 101, 201"
              onChange={handleEditInputChange}
              required
              disabled={isUpdating}
            />
            <small className="helper-text">
              Enter the numeric part of the course code (e.g. 101, 201)
            </small>
          </div>

          {computedEditCourseCode && (
            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> Course
                Code
              </label>
              <input
                type="text"
                value={computedEditCourseCode}
                readOnly={!!editCourse.departmentId || isUpdating}
                onChange={
                  !editCourse.departmentId
                    ? handleComputedEditCourseCodeChange
                    : undefined
                }
                disabled={isUpdating}
              />
              <small className="helper-text">
                This code is auto-generated based on department and suffix.
              </small>
            </div>
          )}

          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Level
            </label>
            <Select
              id="level"
              name="level"
              options={levels}
              value={levels.find((option) => option.value === editCourse.level)}
              onChange={(selectedOption) => {
                setEditCourse((prev) => ({
                  ...prev,
                  level: selectedOption ? selectedOption.value : "",
                }));
              }}
              isClearable
              placeholder="Select level..."
              required
              isDisabled={isUpdating}
            />
          </div>

          {/* CO-PO Mapping Section - Only show when program is selected */}
          {editCourse.programId && selectedEditProgramData && (
            <COPOMapping
              courseOutcomes={editCourse.outcomes}
              programOutcomes={selectedEditProgramData.programOutcomes || []}
              copoMapping={editCourse.COPOMapping}
              onCOPOMappingChange={handleEditCOPOMappingChange}
              onCOChange={handleEditCOChange}
              onCOAdd={handleEditCOAdd}
              onCORemove={handleEditCORemove}
              isDisabled={isUpdating}
            />
          )}

          {isUpdating ? (
            <Loader />
          ) : (
            <button
              type="submit"
              className="btn-submit"
              disabled={!isEditChanged || isUpdating}
              onClick={(e) => {
                if (!isEditChanged && !isUpdating) {
                  e.preventDefault();
                  swalWarning("No Changes to be saved!");
                }
              }}
            >
              Update Course
            </button>
          )}
        </form>
      </Modal>
    </>
  );
});

export default ManageCourses;
